import type { AudioSettings } from '../audio/audio';
import { normalizeLocale } from '../i18n/i18n-service';
import type { AppLocale } from '../i18n/types';

export interface AppSettings extends AudioSettings {
  reducedMotion: boolean;
  quality: 'auto' | 'low' | 'high';
  shareAnonymousMetrics: boolean;
  uploadDiagnosticLogs: boolean;
  locale: AppLocale | null;
}

export const DEFAULT_APP_SETTINGS: AppSettings = {
  musicVolume: 0.42,
  sfxVolume: 0.78,
  muted: false,
  reducedMotion: false,
  quality: 'auto',
  shareAnonymousMetrics: true,
  uploadDiagnosticLogs: true,
  locale: null,
};

const validVolume = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

export function parseSavedSettings(raw: string | null): AppSettings {
  if (!raw) return { ...DEFAULT_APP_SETTINGS };
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    return {
      musicVolume: validVolume(value.musicVolume) ? value.musicVolume : DEFAULT_APP_SETTINGS.musicVolume,
      sfxVolume: validVolume(value.sfxVolume) ? value.sfxVolume : DEFAULT_APP_SETTINGS.sfxVolume,
      muted: typeof value.muted === 'boolean' ? value.muted : DEFAULT_APP_SETTINGS.muted,
      reducedMotion: typeof value.reducedMotion === 'boolean' ? value.reducedMotion : DEFAULT_APP_SETTINGS.reducedMotion,
      quality: value.quality === 'low' || value.quality === 'high' || value.quality === 'auto' ? value.quality : DEFAULT_APP_SETTINGS.quality,
      shareAnonymousMetrics: typeof value.shareAnonymousMetrics === 'boolean' ? value.shareAnonymousMetrics : DEFAULT_APP_SETTINGS.shareAnonymousMetrics,
      uploadDiagnosticLogs: typeof value.uploadDiagnosticLogs === 'boolean' ? value.uploadDiagnosticLogs : DEFAULT_APP_SETTINGS.uploadDiagnosticLogs,
      locale: normalizeLocale(value.locale),
    };
  } catch {
    return { ...DEFAULT_APP_SETTINGS };
  }
}

export function saveSettings(storage: Pick<Storage, 'setItem'>, settings: AppSettings): boolean {
  try {
    storage.setItem('towerarena.com.settings', JSON.stringify(settings));
    return true;
  } catch {
    return false;
  }
}
